import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { CAlert, CButton, CSpinner } from '@coreui/react';
import { api, problemMessage, type Schemas } from '../api/client';
import { formatDateTime } from '../format';
import logo from '../assets/logo.svg';
import './guide.css';

type Guide = Schemas['GuideResponse'];

/**
 * Průvodka — papír, se kterým sestavitel pracuje v dílně. Bez menu a navržená na A4.
 * <p>
 * Data přicházejí už naformátovaná (ANO/NE, české datum, parametry): je to
 * prezentační projekce ze serveru, frontend jen kreslí. Nejvýraznější věc na stránce je
 * varování o nedořešeném dotazu nebo úkolu — jinak se postaví něco jiného, než se čekalo.
 */
export function GuidePage() {
  const id = Number(useParams().id);
  const [guide, setGuide] = useState<Guide | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.GET('/api/v1/contracts/{contractId}/guide-data', { params: { path: { contractId: id } } })
      .then(({ data, error: problem }) => (data ? setGuide(data) : setError(problemMessage(problem))));
  }, [id]);

  if (error) return <CAlert color="danger" className="m-4">{error}</CAlert>;
  if (!guide) return <div className="p-4"><CSpinner color="primary" /></div>;

  return (
    <div className="guide">
      <div className="guide-actions">
        <Link to={`/zakazky/${id}`}>← Zpět na zakázku</Link>
        <CButton color="primary" onClick={() => window.print()}>Tisknout</CButton>
      </div>

      <header className="guide-header">
        <img src={logo} alt="Statotest" width={96} />
        <div className="text-end">
          <div className="guide-label">Průvodka</div>
          <div className="guide-number">{guide.contractNumber}</div>
        </div>
      </header>

      <h1 className="guide-title">{guide.title}</h1>

      {guide.warnings.length > 0 && (
        <section className="guide-warnings">
          {guide.warnings.map((w) => <div key={w}>{w}</div>)}
        </section>
      )}

      <table className="guide-table">
        <tbody>
          {guide.header.map((row) => (
            <tr key={row.label}><th>{row.label}</th><td>{row.value}</td></tr>
          ))}
        </tbody>
      </table>

      {guide.items.map((item) => (
        <section key={item.lineNumber} className="guide-item">
          <h2>Položka #{item.lineNumber} — {item.deviceType} — {item.quantity}</h2>
          <table className="guide-table">
            <tbody>
              {item.configuration.map((row) => (
                <tr key={row.label}>
                  <th>{row.label}</th>
                  <td className={row.value === 'NENÍ VYPLNĚNA' ? 'guide-missing' : undefined}>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {item.parameters.length > 0 && (
            <table className="guide-table guide-parameters">
              <tbody>
                {item.parameters.map((row) => (
                  <tr key={row.label}><th>{row.label}</th><td>{row.value}</td></tr>
                ))}
              </tbody>
            </table>
          )}
          {item.note && <p className="guide-note">{item.note}</p>}
        </section>
      ))}

      {guide.notes.length > 0 && (
        <section className="guide-item">
          <h2>Poznámky</h2>
          {guide.notes.map((n) => <p key={n} className="guide-note">{n}</p>)}
        </section>
      )}

      <footer className="guide-footer">
        Vytiskl(a) {guide.generatedBy} · {formatDateTime(guide.generatedAt)} · stav: {guide.state}
      </footer>
    </div>
  );
}
